// Authenticated fetch for Spectra model-service calls.
//
// Every model service (MatterGen, FM4M, GT4SD, Analyze, DFT, QE) now verifies a
// Firebase ID token. This helper attaches the current user's token as a Bearer
// header and applies a default timeout so a hung/slow service can't block the UI
// indefinitely. A caller-supplied AbortSignal (e.g. run cancellation) is merged
// with the timeout — either can abort the request.

import { auth } from '../config/firebase';

export async function authHeaders() {
  try {
    const token = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

export async function authedFetch(url, opts = {}) {
  const { timeout = 120000, signal, headers, ...rest } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeout);
  if (signal) {
    if (signal.aborted) ctrl.abort();
    else signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  try {
    const ah = await authHeaders();
    return await fetch(url, {
      ...rest,
      headers: { ...ah, ...(headers || {}) },
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Decide which auth headers (if any) a fetch to `url` should carry: `/relay/` traffic uses the
 * caller's relay (BYOC) headers, `/models/:id` traffic uses the custom-model headers, anything
 * else gets none. I8: custom-model result-file fetches (`${proxyBaseUrl}/models/:id/...`)
 * previously matched neither predicate and got a bare, unauthenticated fetch — the resulting 401
 * was swallowed by the caller's retry loop and silently fell back to a binary-download stub.
 * Pure — no I/O, no React.
 */
export function authHeadersForUrl(url, { relayHeaders, modelsHeaders } = {}) {
  if (!url || typeof url !== 'string') return undefined;
  if (url.includes('/relay/')) return relayHeaders || undefined;
  if (url.includes('/models/')) return modelsHeaders || undefined;
  return undefined;
}
